import React, { useMemo } from 'react';
import { getIntl } from '../../../../../utils/intl';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import useMedicalProfileData from '../../../../../hooks/api/useMedicalProfileData';
import { PromptActions, PromptContainer } from '../../../../v2BaseComponents/Prompt';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { getSpacing } from '../../../../../utils/spacing';

const intl = getIntl('DeleteVisitSeriesPrompt', {
    title: 'Delete Recurring Visit',
    body: 'This visit is part of a recurring series.',
    deleteThis: 'Delete this visit only',
    deleteFuture: 'Delete this and all future visits',
    cancel: 'Cancel',
});

/**
 * Delete options for a visit that belongs to a recurring series. "All future" covers every
 * loaded visit in the same series starting on or after this one.
 */
const DeleteVisitSeriesPrompt: React.FC<{
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    profileId: string;
    visit: MedicalDataTypes.PrimaryRecordVisit;
    onDeleteSingle: () => void;
    onSeriesDeleted: () => void;
}> = ({ isOpen, setIsOpen, profileId, visit, onDeleteSingle, onSeriesDeleted }) => {
    const { resources, bulkUpdateResources, isBulkUpdatingResources, isLoadingResources } = useMedicalProfileData({
        medicalProfileId: profileId,
        resourceCategory: 'visit',
        onBulkUpdateSuccess: () => {
            setIsOpen(false);
            onSeriesDeleted();
        },
    });

    const seriesResourceIds = useMemo(
        () =>
            resources.resources
                .filter(
                    (_) =>
                        _.type === 'visit' &&
                        _.recurrenceGroupId === visit.recurrenceGroupId &&
                        !!_.startDateTime &&
                        !!visit.startDateTime &&
                        _.startDateTime >= visit.startDateTime,
                )
                .map((_) => _.id),
        [resources.resources, visit],
    );

    const handleDeleteFuture = () => {
        if (!seriesResourceIds.length) return;
        bulkUpdateResources({
            action: 'delete',
            resourceCategory: 'visit',
            resourceIds: seriesResourceIds,
        });
    };

    return (
        <PromptContainer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            renderPromptContents={() => (
                <Stack vertical fullWidth space={contentStackSpacing} padding={getSpacing(6)}>
                    <Text variant="h4">{intl('title')}</Text>
                    <Text variant="body-1">{intl('body')}</Text>
                    <PromptActions
                        actions={[
                            {
                                title: intl('deleteThis'),
                                onPress: () => {
                                    setIsOpen(false);
                                    onDeleteSingle();
                                },
                                isDisabled: isBulkUpdatingResources,
                                testID: 'delete-visit-series-this',
                            },
                            {
                                title: intl('deleteFuture'),
                                onPress: handleDeleteFuture,
                                // Disabled until the series members load - deleting against
                                // an empty list would silently do nothing.
                                isDisabled: isBulkUpdatingResources || isLoadingResources || !seriesResourceIds.length,
                                testID: 'delete-visit-series-future',
                            },
                            {
                                title: intl('cancel'),
                                variant: 'secondary',
                                onPress: () => setIsOpen(false),
                                testID: 'delete-visit-series-cancel',
                            },
                        ]}
                    />
                </Stack>
            )}
        />
    );
};

export default React.memo(DeleteVisitSeriesPrompt);
